import {Editor, useEditorState} from '@tiptap/react';
import styles from './TiptapToolbar.module.scss';
import {ButtonGroup} from '@trussworks/react-uswds';
import {capitalizeFirstLetter, small_tooltip_style} from '../../../lib/utils';
import {rtf_color_dict} from '../../../../common/utils/rtf-utils';
import {Tooltip} from 'react-tooltip';
import {useId} from 'react';
import ButtonWithTooltip from '../../FormComponents/ButtonWithTooltip/ButtonWithTooltip';
import TiptapLinkPopover from '../TiptapLinkPopover/TiptapLinkPopover';

interface TiptapToolbarProps {
  editor: Editor;
}

const heading_levels = [1, 2, 3] as const;
const colors = Object.keys(rtf_color_dict) as (keyof typeof rtf_color_dict)[];

const getDefaultEditorState = () => ({
  is_headings: heading_levels.map(() => false),
  can_headings: heading_levels.map(() => false),
  is_bold: false,
  can_bold: false,
  is_italic: false,
  can_italic: false,
  is_underline: false,
  can_underline: false,
  is_bullet_list: false,
  can_bullet_list: false,
  is_ordered_list: false,
  can_ordered_list: false,
  can_hr: false,
  is_uncolored: false,
  can_uncolor: false,
  is_colors: colors.map(() => false),
  can_color: false,
  current_color: '#000000',
  is_link: false,
  can_link: false,
});

export default function TiptapToolbar({editor}: TiptapToolbarProps) {
  const color_input_id = useId();
  const link_button_id = useId();

  const editor_state = useEditorState({
    editor,
    selector: ({editor}) => {
      const state = getDefaultEditorState();

      if (!editor.isInitialized || !editor.isEditable) {
        return state;
      }

      const current_color = editor.getAttributes('textStyle').color as unknown;

      // TODO: In practice, we find that .toggleBulletList() works fine when orderedList is active, and similarly
      // .toggleOrderedList() works fine when bulletList is active. Unfortunately, the .can() chains report otherwise.
      // Manually set the "can" behavior of these list buttons until the issue is better understood.
      const is_bullet_list = editor.isActive('bulletList');
      const is_ordered_list = editor.isActive('orderedList');

      state.is_headings = heading_levels.map((level) => editor.isActive('heading', {level}));
      state.can_headings = heading_levels.map((level) =>
        editor.can().chain().focus().toggleHeading({level}).run()
      );
      state.is_bold = editor.isActive('bold');
      state.can_bold = editor.can().chain().focus().toggleBold().run();
      state.is_italic = editor.isActive('italic');
      state.can_italic = editor.can().chain().focus().toggleItalic().run();
      state.is_underline = editor.isActive('underline');
      state.can_underline = editor.can().chain().focus().toggleUnderline().run();
      state.is_bullet_list = is_bullet_list;
      state.can_bullet_list =
        is_ordered_list || editor.can().chain().focus().toggleBulletList().run();
      state.is_ordered_list = is_ordered_list;
      state.can_ordered_list =
        is_bullet_list || editor.can().chain().focus().toggleOrderedList().run();
      state.can_hr = editor.can().chain().focus().setHorizontalRule().run();
      state.is_uncolored = !current_color;
      state.can_uncolor = editor.can().chain().focus().unsetColor().run();
      state.is_colors = colors.map((color) =>
        editor.isActive('textStyle', {color: rtf_color_dict[color]})
      );
      state.can_color = editor.can().chain().focus().setColor('#ffffff').run();
      state.current_color =
        typeof current_color == 'string' && /^#[0-9a-f]{6}$/i.test(current_color)
          ? current_color
          : '#000000';
      state.is_link = editor.isActive('link');
      state.can_link = editor.can().chain().focus().toggleLink().run();

      return state;
    },
  });

  return (
    <>
      <div className={`display-flex flex-wrap ${styles.toolbar} margin-bottom-05`}>
        <ButtonGroup className="usa-button-group--segmented margin-right-1 flex-auto">
          {heading_levels.map((level, idx) => (
            <ButtonWithTooltip
              key={`heading-level-${level}`}
              is_outline
              onClick={() => editor.chain().focus().toggleHeading({level}).run()}
              is_disabled={!editor_state.can_headings[idx]}
              className={editor_state.is_headings[idx] ? 'is-active' : ''}
              tooltip_text={`Heading level ${level}`}
              tooltip_place="top"
              is_small_tooltip
            >
              H<sub>{level}</sub>
            </ButtonWithTooltip>
          ))}
        </ButtonGroup>
        <ButtonGroup className="usa-button-group--segmented margin-right-1">
          <ButtonWithTooltip
            is_outline
            onClick={() => editor.chain().focus().toggleBold().run()}
            is_disabled={!editor_state.can_bold}
            className={editor_state.is_bold ? 'is-active' : ''}
            tooltip_text="Bold"
            tooltip_place="top"
            is_small_tooltip
          >
            B
          </ButtonWithTooltip>
          <ButtonWithTooltip
            is_outline
            onClick={() => editor.chain().focus().toggleItalic().run()}
            is_disabled={!editor_state.can_italic}
            className={editor_state.is_italic ? 'is-active' : ''}
            tooltip_text="Italic"
            tooltip_place="top"
            is_small_tooltip
          >
            <span className="text-italic">I</span>
          </ButtonWithTooltip>
          <ButtonWithTooltip
            is_outline
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            is_disabled={!editor_state.can_underline}
            className={editor_state.is_underline ? 'is-active' : ''}
            tooltip_text="Underline"
            tooltip_place="top"
            is_small_tooltip
          >
            <span className="text-underline">U</span>
          </ButtonWithTooltip>
        </ButtonGroup>
        <ButtonGroup className="usa-button-group--segmented margin-right-1">
          <ButtonWithTooltip
            is_outline
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            is_disabled={!editor_state.can_bullet_list}
            className={`svg-button ${editor_state.is_bullet_list ? 'is-active' : ''}`}
            tooltip_text="Unordered list"
            tooltip_place="top"
            is_small_tooltip
          >
            {
              // Google Material Design Icon: Format List Bulleted (Apache License Version 2.0)
              // https://fonts.google.com/icons?icon.query=format+list&icon.size=24&icon.color=%23000000&selected=Material+Symbols+Outlined:format_list_bulleted:FILL@0;wght@400;GRAD@0;opsz@24
            }
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="1rem"
              width="1rem"
              viewBox="0 -960 960 960"
              fill="currentColor"
            >
              <path d="M360-200v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360ZM200-160q-33 0-56.5-23.5T120-240q0-33 23.5-56.5T200-320q33 0 56.5 23.5T280-240q0 33-23.5 56.5T200-160Zm0-240q-33 0-56.5-23.5T120-480q0-33 23.5-56.5T200-560q33 0 56.5 23.5T280-480q0 33-23.5 56.5T200-400Zm0-240q-33 0-56.5-23.5T120-720q0-33 23.5-56.5T200-800q33 0 56.5 23.5T280-720q0 33-23.5 56.5T200-640Z" />
            </svg>
          </ButtonWithTooltip>
          <ButtonWithTooltip
            is_outline
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            is_disabled={!editor_state.can_ordered_list}
            className={`svg-button ${editor_state.is_ordered_list ? 'is-active' : ''}`}
            tooltip_text="Ordered list"
            tooltip_place="top"
            is_small_tooltip
          >
            {
              // Google Material Design Icon: Format List Numbered (Apache License Version 2.0)
              // https://fonts.google.com/icons?icon.query=format+list&icon.size=24&icon.color=%23000000&selected=Material+Symbols+Outlined:format_list_numbered:FILL@0;wght@400;GRAD@0;opsz@24
            }
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="1rem"
              width="1rem"
              viewBox="0 -960 960 960"
              fill="currentColor"
            >
              <path d="M120-80v-60h100v-30h-60v-60h60v-30H120v-60h120q17 0 28.5 11.5T280-280v40q0 17-11.5 28.5T240-200q17 0 28.5 11.5T280-160v40q0 17-11.5 28.5T240-80H120Zm0-280v-110q0-17 11.5-28.5T160-510h60v-30H120v-60h120q17 0 28.5 11.5T280-560v70q0 17-11.5 28.5T240-450h-60v30h100v60H120Zm60-280v-180h-60v-60h120v240h-60Zm180 440v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360Z" />
            </svg>
          </ButtonWithTooltip>
        </ButtonGroup>
        <ButtonGroup className="usa-button-group--segmented margin-right-1">
          <ButtonWithTooltip
            is_outline
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            is_disabled={!editor_state.can_hr}
            className="svg-button"
            tooltip_text="Horizontal rule"
            tooltip_place="top"
            is_small_tooltip
          >
            {
              // Google Material Design Icon: Horizontal Rule (Apache License Version 2.0)
              // https://fonts.google.com/icons?icon.query=horizontal+rule&icon.size=24&icon.color=%23000000&selected=Material+Symbols+Outlined:horizontal_rule:FILL@0;wght@400;GRAD@0;opsz@24
            }
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="1rem"
              width="1rem"
              viewBox="0 -960 960 960"
              fill="currentColor"
            >
              <path d="M160-440v-80h640v80H160Z" />
            </svg>
          </ButtonWithTooltip>
          <ButtonWithTooltip
            id={link_button_id}
            is_outline
            onClick={() => undefined}
            is_disabled={!editor_state.can_link}
            className={`svg-button ${editor_state.is_link ? 'is-active' : ''}`}
            tooltip_text="Link"
            tooltip_place="top"
            is_small_tooltip
          >
            {
              // Google Material Design Icon: Link (Apache License Version 2.0)
              // https://fonts.google.com/icons?icon.query=link&icon.size=24&icon.color=%23000000&selected=Material+Symbols+Outlined:link:FILL@0;wght@400;GRAD@0;opsz@24
            }
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="1rem"
              width="1rem"
              viewBox="0 -960 960 960"
              fill="currentColor"
            >
              <path d="M440-280H280q-83 0-141.5-58.5T80-480q0-83 58.5-141.5T280-680h160v80H280q-50 0-85 35t-35 85q0 50 35 85t85 35h160v80ZM320-440v-80h320v80H320Zm200 160v-80h160q50 0 85-35t35-85q0-50-35-85t-85-35H520v-80h160q83 0 141.5 58.5T880-480q0 83-58.5 141.5T680-280H520Z" />
            </svg>
          </ButtonWithTooltip>
        </ButtonGroup>
        <div className="display-flex">
          <ButtonGroup className="usa-button-group--segmented margin-right-1">
            <ButtonWithTooltip
              is_outline
              onClick={() => editor.chain().focus().unsetColor().run()}
              is_disabled={!editor_state.can_uncolor}
              className={`color-button ${editor_state.is_uncolored ? 'is-active' : ''}`}
              tooltip_text="Black"
              tooltip_place="top"
              is_small_tooltip
            >
              <strong className="text-black">A</strong>
            </ButtonWithTooltip>
            {colors.map((color_name, idx) => {
              const color = rtf_color_dict[color_name];
              return (
                <ButtonWithTooltip
                  key={`color-${color_name}`}
                  is_outline
                  onClick={() => editor.chain().focus().setColor(color).run()}
                  is_disabled={!editor_state.can_color}
                  className={`color-button ${editor_state.is_colors[idx] ? 'is-active' : ''}`}
                  tooltip_text={capitalizeFirstLetter(color_name)}
                  tooltip_place="top"
                  is_small_tooltip
                >
                  <strong style={{color}}>A</strong>
                </ButtonWithTooltip>
              );
            })}
          </ButtonGroup>
          <div className="display-flex">
            <input
              id={color_input_id}
              className="flex-align-self-center margin-y-05"
              type="color"
              onChange={(event) => editor.chain().focus().setColor(event.target.value).run()}
              disabled={!editor_state.can_color}
              value={editor_state.current_color}
            />
            <Tooltip
              anchorSelect={`#${CSS.escape(color_input_id)}`}
              place="top"
              style={small_tooltip_style}
            >
              Select color
            </Tooltip>
          </div>
        </div>
      </div>
      <TiptapLinkPopover editor={editor} anchor_select={`#${CSS.escape(link_button_id)}`} />
    </>
  );
}
